import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, MapPin, Camera, ShieldCheck, CheckCircle2 } from "lucide-react";
import { getTheme, fonts } from "../theme";
import Navbar from "../components/Navbar";
import TrustDial from "../components/TrustDial";
import SocialLinks from "../components/SocialLinks";
import BreakdownGrid from "../components/BreakdownGrid";
import PrivateRatingsOnFile from "../components/PrivateRatingsOnFile";
import { Button, Field, inputStyle } from "../components/UI";
import { EMPLOYER_CRITERIA } from "../criteria";
import api, { fileUrl } from "../api";

export default function EmployerDashboard({ user: propUser, onLogout, themeMode = "dark", setThemeMode }) {
  const theme = getTheme("employer", themeMode);

  const user = propUser || (() => {
    try {
      const s = sessionStorage.getItem("wibil_user") || localStorage.getItem("wibil_user");
      return s && s !== "undefined" ? JSON.parse(s) : null;
    } catch (e) { return null; }
  })();

  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [identityPanel, setIdentityPanel] = useState(null);
  const [identityBusy, setIdentityBusy] = useState(false);
  const [identityError, setIdentityError] = useState("");
  const [identityOk, setIdentityOk] = useState("");
  const [gstDraft, setGstDraft] = useState("");
  const [photoPreview, setPhotoPreview] = useState("");
  const photoInputRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!user || !user.id) return;
    setLoadError(null);
    try {
      const res = await api.get(`/employers/${user.id}`);
      setData(res);
    } catch (e) {
      console.error(e);
      setData({
        profile: {
          id: user.id,
          company_name: user.company_name || user.name || "Employer Account",
          email: user.email || "",
          location: user.location || "",
          industry: user.industry || "",
          is_verified: 0,
          gst_number: user.gst_number || ""
        },
        score: { trust_score: 300, rating_count: 0 },
        ratings: []
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const gst = data?.profile?.gst_number || data?.gst_number;
    if (gst) setGstDraft(String(gst).toUpperCase());
  }, [data]);

  if (loadError && !data) {
    return (
      <div style={{ minHeight: "100vh", background: theme.bg, color: theme.text, fontFamily: fonts.body }}>
        <Navbar theme={theme} userName={user?.name} onLogout={onLogout} themeMode={themeMode} setThemeMode={setThemeMode} />
        <div className="container py-5 text-center">
          <div className="alert alert-danger d-inline-block text-start p-4 rounded-4 shadow-sm" style={{ maxWidth: 500 }}>
            <h5 className="fw-bold mb-2">Unable to Load Dashboard</h5>
            <p className="small mb-3">{loadError}</p>
            <div className="d-flex gap-2">
              <button className="btn btn-primary btn-sm" onClick={load}>Retry</button>
              <button className="btn btn-outline-secondary btn-sm" onClick={onLogout}>Sign Out</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return <div style={{ background: theme.bg, minHeight: "100vh", color: theme.textMute, padding: 40 }}>Loading employer dashboard…</div>;

  const profile = data.profile || (data.company_name ? data : null) || {};
  const companyName = profile.company_name || profile.name || user?.name || "Employer Account";
  const ratings = Array.isArray(data.ratings) ? data.ratings : [];
  const score = scoreFromEmployeeRatings(ratings);
  const hasWorkplaceScores = ratings.length > 0;
  const isLight = theme.key === "light";
  const gstValue = String(profile.gst_number || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  const gstLocked = gstValue.length === 15;

  const applyIdentityProfile = (next) => {
    setData((prev) => ({
      ...(prev || {}),
      profile: { ...((prev && prev.profile) || prev || {}), ...next }
    }));
  };

  const saveIdentity = async (body) => {
    if (!user?.id) throw new Error("Please sign in to update identity details.");
    const res = await api.updateEmployerIdentity(user.id, body);
    if (res?.profile) applyIdentityProfile(res.profile);
    return res;
  };

  const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Could not read that file."));
    reader.readAsDataURL(file);
  });

  const handlePhotoChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const stored = String(profile.logo || profile.profile_photo || "").trim();
    if (stored || photoPreview) {
      setIdentityError("Profile photo is already set and cannot be changed.");
      e.target.value = "";
      return;
    }
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      setIdentityError("Please upload a JPG, PNG, or WebP photo.");
      return;
    }
    setIdentityBusy(true);
    setIdentityError("");
    setIdentityOk("");
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setPhotoPreview(dataUrl);
      await saveIdentity({ profilePhoto: dataUrl });
      setIdentityOk("Profile photo saved.");
    } catch (err) {
      setIdentityError(err.message || "Could not save profile photo.");
      setPhotoPreview("");
    } finally {
      setIdentityBusy(false);
      e.target.value = "";
    }
  };

  const handleGstSave = async () => {
    if (gstLocked) {
      setIdentityError("GST verification is already complete and cannot be changed.");
      return;
    }
    const gst = String(gstDraft || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
    if (gst.length !== 15) {
      setIdentityError("Please enter a valid 15-character GST number.");
      return;
    }
    setIdentityBusy(true);
    setIdentityError("");
    setIdentityOk("");
    try {
      const verified = await api.post("/verify/gst", { gstNo: gst });
      await saveIdentity({
        gst_number: verified.gstNo || verified.gstin || gst,
        company_name: verified.companyName || verified.corporate_name || verified.trade_name || companyName,
        location: verified.address || verified.registeredAddress || profile.location || ""
      });
      setIdentityOk("GST verified.");
      setIdentityPanel(null);
    } catch (err) {
      setIdentityError(err.message || "GST verification failed.");
    } finally {
      setIdentityBusy(false);
    }
  };

  const storedPhoto = String(profile.logo || profile.profile_photo || "").trim();
  const photoSrc = photoPreview
    || (storedPhoto
      ? (storedPhoto.startsWith("data:") || storedPhoto.startsWith("http") ? storedPhoto : fileUrl(storedPhoto))
      : "")
    || "";
  const photoLocked = Boolean(storedPhoto || photoPreview);
  const updateActionBtnStyle = {
    background: "#0D6EFD",
    color: "#FFFFFF",
    border: "1px solid #0D6EFD",
    fontSize: 11,
    fontWeight: 700,
    padding: "4px 12px",
    borderRadius: 6
  };

  return (
    <div style={{ minHeight: "100vh", background: theme.bg, color: theme.text, fontFamily: fonts.body, transition: "all 0.2s" }}>
      <Navbar theme={theme} userName={companyName} onLogout={onLogout} themeMode={themeMode} setThemeMode={setThemeMode} />

      <div className="container py-4 py-md-5">
        <div
          className="card p-4 rounded-4 shadow-sm mb-4"
          style={{
            background: theme.surface,
            border: `1px solid ${theme.line}`,
            color: theme.text,
            boxShadow: isLight ? "0 4px 20px -2px rgba(0, 0, 0, 0.05)" : "none"
          }}
        >
          <div className="row g-4 align-items-center">
            <div className="col-12 col-md-4 col-lg-3 text-center border-end-md" style={{ borderRight: `1px solid ${theme.line}` }}>
              <TrustDial score={score.trust_score ?? 300} theme={theme} />
            </div>

            <div className="col-12 col-md-8 col-lg-9">
              <div className="d-flex align-items-center gap-3 mb-2">
                <button
                  type="button"
                  onClick={() => { if (!photoLocked) photoInputRef.current?.click(); }}
                  title={photoLocked ? "Profile photo is already set" : "Add profile photo"}
                  className="border-0 p-0 position-relative"
                  disabled={identityBusy || !user?.id || photoLocked}
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    background: "transparent",
                    cursor: !photoLocked && user?.id ? "pointer" : "default",
                    flexShrink: 0
                  }}
                >
                  <span
                    className="d-block w-100 h-100 rounded-circle d-flex align-items-center justify-content-center fw-bold"
                    style={{
                      overflow: "hidden",
                      background: theme.badgeBg,
                      border: `2px solid ${theme.accent}`,
                      color: theme.accent,
                      fontSize: 22
                    }}
                  >
                    {photoSrc ? (
                      <img src={photoSrc} alt={companyName} className="w-100 h-100 object-fit-cover" />
                    ) : (
                      companyName.charAt(0).toUpperCase()
                    )}
                  </span>
                  {!photoLocked && (
                    <span
                      className="position-absolute d-flex align-items-center justify-content-center"
                      style={{
                        right: 0,
                        bottom: 0,
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        background: theme.accent,
                        color: "#fff",
                        border: "2px solid #fff"
                      }}
                    >
                      <Camera size={12} />
                    </span>
                  )}
                </button>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  hidden
                  onChange={handlePhotoChange}
                />

                <div>
                  <h2 className="h4 fw-bold mb-0" style={{ fontFamily: fonts.display, color: theme.text }}>{companyName}</h2>
                  <div className="small d-flex align-items-center gap-2 mt-1" style={{ color: theme.textMute }}>
                    {profile.industry && <span>{profile.industry}</span>}
                    {profile.location && <span className="d-inline-flex align-items-center gap-1"><MapPin size={12} />{profile.location}</span>}
                  </div>
                </div>
              </div>

              {profile.company_description && (
                <p className="small mb-3 p-2 rounded-3" style={{ background: theme.surfaceRaised, color: theme.textMute, border: `1px solid ${theme.line}` }}>
                  {profile.company_description}
                </p>
              )}

              <SocialLinks profile={profile} theme={theme} />

              <div className="p-3 rounded-3 mt-3" style={{ background: isLight ? "#F8FAFC" : "rgba(255,255,255,0.03)", border: `1px solid ${theme.line}` }}>
                <div className="d-flex flex-wrap align-items-center gap-3">
                  <div className="d-flex align-items-center gap-2 small fw-semibold">
                    <CheckCircle2 size={16} color={gstLocked ? "#16A34A" : "#DC2626"} />
                    <span>GST verification:{" "}
                      <strong style={{ color: gstLocked ? "#16A34A" : "#DC2626" }}>
                        {gstLocked ? "Verified" : "Not verified"}
                      </strong>
                    </span>
                    {!gstLocked && (
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={identityBusy || !user?.id}
                        onClick={() => {
                          setIdentityPanel((cur) => (cur === "gst" ? null : "gst"));
                          setIdentityError("");
                          setIdentityOk("");
                        }}
                        style={updateActionBtnStyle}
                      >
                        Update
                      </button>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-2 small fw-semibold">
                    <ShieldCheck size={16} color={photoLocked ? "#16A34A" : "#DC2626"} />
                    <span>Profile photo:{" "}
                      <strong style={{ color: photoLocked ? "#16A34A" : "#DC2626" }}>
                        {photoLocked ? "Verified" : "Not verified"}
                      </strong>
                    </span>
                  </div>
                </div>
                {identityError && <div className="small mt-2 fw-semibold" style={{ color: "#DC2626" }}>{identityError}</div>}
                {identityOk && <div className="small mt-2 fw-semibold" style={{ color: "#16A34A" }}>{identityOk}</div>}
                {identityBusy && <div className="small mt-2" style={{ color: theme.textMute }}>Updating identity details…</div>}
                {identityPanel === "gst" && !gstLocked && (
                  <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${theme.line}` }}>
                    <Field label="GSTIN / GST Number (15 characters)" theme={theme}>
                      <input
                        type="text"
                        maxLength={15}
                        style={inputStyle(theme)}
                        value={gstDraft}
                        onChange={(e) => setGstDraft(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                        placeholder="27ABCDE1234F1Z5"
                        disabled={identityBusy}
                      />
                    </Field>
                    <Button theme={theme} onClick={handleGstSave} disabled={identityBusy || String(gstDraft || "").length !== 15}>
                      {identityBusy ? "Verifying…" : "Verify GST"}
                    </Button>
                  </div>
                )}
              </div>

              <div className="d-flex flex-wrap gap-2 mt-3">
                <Button theme={theme} onClick={() => navigate("/rate/employee")}>
                  <Plus size={14} /> Rate an employee
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-5">
          <SectionTitle theme={theme}>Score breakdown</SectionTitle>
          {hasWorkplaceScores ? (
            <BreakdownGrid criteria={EMPLOYER_CRITERIA} score={score} theme={theme} />
          ) : (
            <div style={{ border: `1px dashed ${theme.line}`, borderRadius: 10, padding: 20, color: theme.textMute, fontSize: 13 }}>
              No employee ratings yet. Score breakdown updates automatically after employees submit a workplace rating.
            </div>
          )}
        </div>

        <PrivateRatingsOnFile
          heading="Private workplace ratings on file"
          ratings={ratings}
          criteria={EMPLOYER_CRITERIA}
          title="Confidential workplace rating"
          emptyText="No private workplace ratings on file yet."
          theme={theme}
        />

      </div>
    </div>
  );
}

function SectionTitle({ children, theme }) {
  return <div style={{ fontFamily: fonts.mono, fontSize: 11, letterSpacing: 1.5, color: theme.textMute, marginBottom: 14, textTransform: "uppercase", fontWeight: 700 }}>{children}</div>;
}

function scoreFromEmployeeRatings(list) {
  const ratings = Array.isArray(list) ? list : [];
  const keys = [
    ["salary", "avg_salary"],
    ["environment", "avg_environment"],
    ["management", "avg_management"],
    ["growth", "avg_growth"],
    ["location_score", "avg_location"]
  ];
  if (!ratings.length) {
    return {
      trust_score: 300,
      rating_count: 0,
      avg_salary: 0,
      avg_environment: 0,
      avg_management: 0,
      avg_growth: 0,
      avg_location: 0
    };
  }
  const avgs = {};
  let pillarSum = 0;
  let pillarCount = 0;
  keys.forEach(([field, avgKey]) => {
    const vals = ratings.map((r) => Number(r[field])).filter((n) => n > 0);
    const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    avgs[avgKey] = Number(avg.toFixed(1));
    vals.forEach((v) => {
      pillarSum += v;
      pillarCount += 1;
    });
  });
  const overall = pillarCount ? pillarSum / pillarCount : 0;
  return {
    ...avgs,
    avg_salary_timeliness: avgs.avg_salary,
    avg_location_score: avgs.avg_location,
    rating_count: ratings.length,
    trust_score: overall > 0 ? Math.round(300 + overall * 65) : 300
  };
}
